import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Thermometer, X, Battery, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface TemperatureHistoryModalProps {
  isOpen: boolean;
  deviceId: string | null;
  deviceName?: string | null;
  tempMin?: number | null;
  tempMax?: number | null;
  onClose: () => void;
}

interface TemperaturePoint {
  created_at: string;
  temperature: number;
  battery_percentage: number | null;
}

type TimeWindow = '1d' | '1w' | '1m';

const TemperatureHistoryModal: React.FC<TemperatureHistoryModalProps> = ({
  isOpen,
  deviceId,
  deviceName,
  tempMin,
  tempMax,
  onClose,
}) => {
  const isDev = process.env.NODE_ENV !== 'production';
  const [points, setPoints] = useState<TemperaturePoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('1d');
  const [hoverPoint, setHoverPoint] = useState<{
    x: number;
    y: number;
    point: TemperaturePoint;
  } | null>(null);
  const [derivedThresholds, setDerivedThresholds] = useState<{
    min?: number | null;
    max?: number | null;
  }>({});

  const handleExportCsv = () => {
    if (!points.length || !deviceId) return;

    // Columns: Device label (Nickname + ID), Time, Temperature, Battery
    const headers = ['Device', 'Time (ISO)', 'Temperature (°C)', 'Battery (%)'];
    const escape = (value: any) => {
      if (value === null || value === undefined) return '';
      const s = String(value);
      if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const deviceLabel =
      (deviceName && deviceName.trim().length > 0
        ? `${deviceName.trim()} (${deviceId})`
        : deviceId);

    const lines = points.map((p) => {
      const timeIso = new Date(p.created_at).toISOString();
      return [
        deviceLabel,
        timeIso,
        p.temperature,
        p.battery_percentage ?? '',
      ]
        .map(escape)
        .join(',');
    });

    const csvContent = '\uFEFF' + [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const datePart = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
    link.download = `temperature_history_${deviceId}_${datePart}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    let cancelled = false;

    const loadTemperatureHistory = async () => {
      if (!isOpen || !deviceId) {
        setPoints([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const now = new Date();
        let fromDate: Date;
        switch (timeWindow) {
          case '1w':
            fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '1m':
            fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case '1d':
          default:
            fromDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        }

        const { data, error: queryError } = await supabase
          .from('gps_data')
          .select('created_at, temperature, battery_percentage')
          .eq('device_id', deviceId)
          .gte('created_at', fromDate.toISOString())
          .order('created_at', { ascending: true });

        if (queryError) {
          throw queryError;
        }

        const rows = (data || []).filter(
          (row: any) =>
            row &&
            typeof row.temperature === 'number' &&
            !Number.isNaN(row.temperature),
        ) as TemperaturePoint[];

        // Find last time the device reported 0% battery within the window.
        // After that point we start a "new cycle" and clear previous history.
        let lastZeroAt: string | null = null;
        rows.forEach((row) => {
          if (
            typeof row.battery_percentage === 'number' &&
            row.battery_percentage === 0
          ) {
            lastZeroAt = row.created_at;
          }
        });

        const filtered =
          lastZeroAt === null
            ? rows
            : rows.filter((row) => row.created_at > lastZeroAt!);

        if (!cancelled) {
          setPoints(filtered);
        }
      } catch (e: any) {
        console.error('Error loading temperature history:', e);
        if (!cancelled) {
          setError(
            e?.message || 'Failed to load temperature history for this device.',
          );
          setPoints([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTemperatureHistory();

    return () => {
      cancelled = true;
    };
  }, [isOpen, deviceId, timeWindow]);

  // Derive min/max thresholds from props or device_config when modal opens
  useEffect(() => {
    let cancelled = false;

    const loadThresholds = async () => {
      if (!isOpen || !deviceId) {
        if (!cancelled) setDerivedThresholds({});
        return;
      }

      try {
        // חשוב: ייתכנו כמה שורות ל‑device_id הזה (היסטוריית קונפיגורציות).
        // כדי שמשתמש רגיל וסופר‑אדמין יראו אותו דבר, נבחר תמיד את ההגדרה העדכנית ביותר.
        const { data, error } = await supabase
          .from('device_config')
          .select('raw_config, created_at, last_updated')
          .eq('device_id', deviceId)
          .order('last_updated', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        if (error || !data) {
          if (isDev) {
            console.log('[TempHistory] No device_config row found for device', {
              deviceId,
              error,
            });
          }
          if (!cancelled) setDerivedThresholds({});
          return;
        }

        if (isDev) {
          console.log('[TempHistory] device_config row selected', {
            deviceId,
            created_at: (data as any)?.created_at,
            last_updated: (data as any)?.last_updated,
          });
        }

        let raw: any = data.raw_config;
        if (typeof raw === 'string') {
          try {
            raw = JSON.parse(raw);
          } catch {
            // ignore parse error
            if (isDev) {
              console.warn('[TempHistory] Failed to parse raw_config JSON string', {
                deviceId,
                raw_config_snippet: String(raw).slice(0, 200),
              });
            }
          }
        }

        const minValRaw =
          raw?.temp_threshold_low ??
          raw?.temperature_min ??
          raw?.temp_min ??
          null;
        const maxValRaw =
          raw?.temp_threshold_high ??
          raw?.temperature_max ??
          raw?.temp_max ??
          null;

        const parsedMin =
          typeof minValRaw === 'number'
            ? minValRaw
            : minValRaw != null
            ? Number(minValRaw)
            : null;
        const parsedMax =
          typeof maxValRaw === 'number'
            ? maxValRaw
            : maxValRaw != null
            ? Number(maxValRaw)
            : null;

        // If DB has no thresholds, fall back to props (if provided)
        const minVal =
          typeof parsedMin === 'number' && !Number.isNaN(parsedMin)
            ? parsedMin
            : typeof tempMin === 'number'
            ? tempMin
            : null;
        const maxVal =
          typeof parsedMax === 'number' && !Number.isNaN(parsedMax)
            ? parsedMax
            : typeof tempMax === 'number'
            ? tempMax
            : null;

        if (isDev) {
          console.log('[TempHistory] Loaded thresholds from device_config', {
            deviceId,
            minValRaw,
            maxValRaw,
            parsedMin,
            parsedMax,
            fallbackFromProps: {
              used:
                (minVal !== parsedMin && typeof tempMin === 'number') ||
                (maxVal !== parsedMax && typeof tempMax === 'number'),
              tempMinProp: tempMin,
              tempMaxProp: tempMax,
            },
            resolved: { minVal, maxVal },
            source: 'device_config.raw_config',
          });
        }

        // Extra diagnostics: see if multiple rows exist with different thresholds
        try {
          const { data: rows } = await supabase
            .from('device_config')
            .select('created_at, last_updated, raw_config')
            .eq('device_id', deviceId)
            .order('last_updated', { ascending: false, nullsFirst: false })
            .order('created_at', { ascending: false })
            .limit(5);
          if (rows && rows.length > 1) {
            const summary = rows.map((r: any) => {
              let rc: any = r.raw_config;
              if (typeof rc === 'string') {
                try { rc = JSON.parse(rc); } catch {}
              }
              return {
                created_at: r.created_at,
                last_updated: r.last_updated,
                low: rc?.temp_threshold_low ?? rc?.temperature_min ?? rc?.temp_min,
                high: rc?.temp_threshold_high ?? rc?.temperature_max ?? rc?.temp_max,
              };
            });
            if (isDev) {
              console.log('[TempHistory] device_config recent rows (threshold summary)', {
                deviceId,
                rows: summary,
              });
            }
          }
        } catch {}

        if (!cancelled) {
          setDerivedThresholds({ min: minVal, max: maxVal });
        }
      } catch {
        console.error('[TempHistory] Failed to load thresholds from device_config', {
          deviceId,
        });
        // If DB fetch failed, fall back to props so we still draw something
        if (!cancelled) {
          if (typeof tempMin === 'number' || typeof tempMax === 'number') {
            setDerivedThresholds({ min: tempMin ?? null, max: tempMax ?? null });
            if (isDev) {
              console.log('[TempHistory] Falling back to props thresholds (DB error)', {
                deviceId,
                tempMinProp: tempMin,
                tempMaxProp: tempMax,
              });
            }
          } else {
            setDerivedThresholds({});
          }
        }
      }
    };

    loadThresholds();

    return () => {
      cancelled = true;
    };
  }, [isOpen, deviceId, tempMin, tempMax]);

  // Prefer canonical thresholds derived from device_config so that super admin
  // and regular users see the same lines for the same physical device.
  const effectiveTempMin =
    typeof derivedThresholds.min === 'number'
      ? derivedThresholds.min
      : typeof tempMin === 'number'
      ? tempMin
      : undefined;
  const effectiveTempMax =
    typeof derivedThresholds.max === 'number'
      ? derivedThresholds.max
      : typeof tempMax === 'number'
      ? tempMax
      : undefined;

  useEffect(() => {
    if (!isOpen || !deviceId) return;
    if (isDev) {
      console.log('[TempHistory] Effective thresholds resolved', {
        deviceId,
        tempMinProp: tempMin,
        tempMaxProp: tempMax,
        derivedMin: derivedThresholds.min,
        derivedMax: derivedThresholds.max,
        effectiveTempMin,
        effectiveTempMax,
      });
    }
  }, [isOpen, deviceId, tempMin, tempMax, derivedThresholds.min, derivedThresholds.max, effectiveTempMin, effectiveTempMax]);

  const chartData = useMemo(() => {
    if (!points.length) return null;

    const times = points.map((p) => new Date(p.created_at).getTime());
    const temps = points.map((p) => p.temperature);

    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const minTemp = Math.min(...temps);
    const maxTemp = Math.max(...temps);

    // Avoid division by zero in flat-line scenarios
    const timeSpan = maxTime - minTime || 1;
    const tempSpan = maxTemp - minTemp || 1;

    const width = 600;
    const height = 220;
    const paddingLeft = 40;
    const paddingRight = 10;
    const paddingTop = 20;
    const paddingBottom = 30;
    const innerWidth = width - paddingLeft - paddingRight;
    const innerHeight = height - paddingTop - paddingBottom;

    const samples = points.map((p) => {
      const t = new Date(p.created_at).getTime();
      const x =
        paddingLeft + ((t - minTime) / timeSpan) * (innerWidth || 1);
      const y =
        paddingTop +
        (1 - (p.temperature - minTemp) / tempSpan) * (innerHeight || 1);
      return { x, y, point: p };
    });

    const path = samples.map((s) => `${s.x},${s.y}`).join(' ');

    const latest = points[points.length - 1];

    return {
      width,
      height,
      paddingLeft,
      paddingBottom,
      paddingTop,
      minTemp,
      maxTemp,
      minTime,
      maxTime,
      path,
      samples,
      latest,
    };
  }, [points]);

  if (!isOpen || !deviceId) {
    return null;
  }

  const titleDevice =
    deviceName || deviceId ? `Device ${deviceName || deviceId}` : 'Device';

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4 modal-overlay">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl modal-content">
        <div className="p-4 pt-5 space-y-3 overflow-y-auto max-h-[calc(90vh-40px)]">
          {/* Controls */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-2 text-sm text-traqpack-gray-700">
              <div className="w-8 h-8 bg-traqpack-gray-100 rounded-xl flex items-center justify-center">
                <Thermometer className="w-4 h-4 text-traqpack-gray-600" />
              </div>
              <span>Time window:</span>
              <div className="inline-flex rounded-full bg-traqpack-gray-100 p-0.5">
                {(
                  [
                    ['1d', '1 Day'],
                    ['1w', '1 Week'],
                    ['1m', '1 Month'],
                  ] as [TimeWindow, string][]
                ).map(([value, label]) => (
                  <button
                    key={value}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      timeWindow === value
                        ? 'bg-white text-traqpack-gray-900 shadow-sm'
                        : 'text-traqpack-gray-600 hover:text-traqpack-gray-900'
                    }`}
                    onClick={() => setTimeWindow(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {chartData && (typeof effectiveTempMin === 'number' || typeof effectiveTempMax === 'number') && (
                <div className="flex flex-col items-end space-y-0.5 text-[10px] text-traqpack-gray-700 mr-1">
                  <div className="flex items-center space-x-2">
                    {typeof effectiveTempMin === 'number' && (
                      <span>
                        Temperature Min{' '}
                        <span className="font-semibold">{effectiveTempMin}°C</span>
                      </span>
                    )}
                    {typeof effectiveTempMax === 'number' && (
                      <span>
                        Temperature Max{' '}
                        <span className="font-semibold">{effectiveTempMax}°C</span>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {chartData?.latest && (
                <div className="flex items-center space-x-3 text-xs text-traqpack-gray-700">
                  <div className="flex items-center space-x-1">
                    <Thermometer className="w-3 h-3 text-blue-500" />
                    <span className="font-semibold">
                      {chartData.latest.temperature}°C
                    </span>
                  </div>
                  {typeof chartData.latest.battery_percentage === 'number' && (
                    <div className="flex items-center space-x-1">
                      <Battery className="w-3 h-3 text-green-500" />
                      <span>{chartData.latest.battery_percentage}%</span>
                    </div>
                  )}
                </div>
              )}

              {/* Export CSV for Excel */}
              <button
                type="button"
                onClick={handleExportCsv}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors"
                title="Export temperature history to CSV"
              >
                <Download className="w-4 h-4" />
              </button>

              {/* Close button aligned to the far right */}
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-900 hover:bg-gray-100 transition-colors"
                aria-label="Close temperature chart"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Chart / States */}
          {loading ? (
            <div className="h-56 flex items-center justify-center text-sm text-traqpack-gray-600">
              Loading temperature data...
            </div>
          ) : error ? (
            <div className="h-56 flex items-center justify-center text-sm text-red-600">
              {error}
            </div>
          ) : !points.length ? (
            <div className="h-56 flex flex-col items-center justify-center text-sm text-traqpack-gray-600 text-center px-4">
              <p className="font-medium mb-1">No temperature data available</p>
              <p className="text-xs">
                The graph starts from the last measurement cycle after the
                device reports again following a 0% battery event.
              </p>
            </div>
          ) : chartData ? (
            <div className="space-y-3">
              <div className="bg-traqpack-gray-50 rounded-2xl p-3">
                <svg
                  viewBox={`0 0 ${chartData.width} ${chartData.height}`}
                  className="w-full h-56"
                  role="img"
                  aria-label="Temperature over time"
                  onMouseLeave={() => setHoverPoint(null)}
                >
                  {/* Grid & ticks */}
                  {(() => {
                    const elements = [];
                    const yTicks = 4;
                    for (let i = 0; i <= yTicks; i++) {
                      const ratio = i / yTicks;
                      const y =
                        chartData.paddingTop +
                        (1 - ratio) *
                          (chartData.height -
                            chartData.paddingTop -
                            chartData.paddingBottom);
                      const value =
                        chartData.minTemp +
                        (chartData.maxTemp - chartData.minTemp || 1) * ratio;
                      elements.push(
                        <g key={`y-${i}`}>
                          <line
                            x1={chartData.paddingLeft}
                            y1={y}
                            x2={chartData.width - 10}
                            y2={y}
                            stroke="#E5E7EB"
                            strokeWidth={1}
                          />
                          <text
                            x={chartData.paddingLeft - 6}
                            y={y + 2}
                            textAnchor="end"
                            fontSize="6"
                            fill="#6B7280"
                          >
                            {value.toFixed(1)}
                          </text>
                        </g>,
                      );
                    }

                    const xTicks = 4;
                    const span = chartData.maxTime - chartData.minTime || 1;
                    for (let i = 0; i <= xTicks; i++) {
                      const ratio = i / xTicks;
                      const t = chartData.minTime + ratio * span;
                      const rawX =
                        chartData.paddingLeft +
                        ratio *
                          (chartData.width -
                            chartData.paddingLeft -
                            10);
                      const d = new Date(t);
                      const label = `${(d.getMonth() + 1)
                        .toString()
                        .padStart(2, '0')}/${d
                        .getDate()
                        .toString()
                        .padStart(2, '0')} ${d
                        .getHours()
                        .toString()
                        .padStart(2, '0')}:${d
                        .getMinutes()
                        .toString()
                        .padStart(2, '0')}`;

                      // התאמת מיקום וטקסט‑עוגן כדי שלא ייחתכו בקצוות
                      const isFirst = i === 0;
                      const isLast = i === xTicks;
                      const labelAnchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                      const labelX = isFirst
                        ? rawX + 2
                        : isLast
                          ? rawX - 2
                          : rawX;

                      elements.push(
                        <g key={`x-${i}`}>
                          <line
                            x1={rawX}
                            y1={chartData.height - chartData.paddingBottom}
                            x2={rawX}
                            y2={chartData.paddingTop}
                            stroke="#EEF2FF"
                            strokeWidth={1}
                          />
                          <text
                            x={labelX}
                            y={chartData.height - chartData.paddingBottom + 6}
                            textAnchor={labelAnchor as 'start' | 'middle' | 'end'}
                            fontSize="6"
                            fill="#6B7280"
                          >
                            {label}
                          </text>
                        </g>,
                      );
                    }
                    return elements;
                  })()}

                  {/* Axes */}
                  <line
                    x1={chartData.paddingLeft}
                    y1={10}
                    x2={chartData.paddingLeft}
                    y2={chartData.height - chartData.paddingBottom}
                    stroke="#CBD5E1"
                    strokeWidth="1"
                  />
                  <line
                    x1={chartData.paddingLeft}
                    y1={chartData.height - chartData.paddingBottom}
                    x2={chartData.width - 10}
                    y2={chartData.height - chartData.paddingBottom}
                    stroke="#CBD5E1"
                    strokeWidth="1"
                  />

                  {/* Axis labels */}
                  <text
                    x={chartData.paddingLeft - 30}
                    y={
                      (chartData.paddingTop +
                        chartData.height -
                        chartData.paddingBottom) /
                      2
                    }
                    textAnchor="middle"
                    fontSize="7"
                    fill="#374151"
                    transform={`rotate(-90 ${
                      chartData.paddingLeft - 30
                    } ${
                      (chartData.paddingTop +
                        chartData.height -
                        chartData.paddingBottom) /
                      2
                    })`}
                  >
                    Temperature (°C)
                  </text>
                  <text
                    x={(chartData.paddingLeft + chartData.width - 10) / 2}
                    y={chartData.height - 12}
                    textAnchor="middle"
                    fontSize="7"
                    fill="#374151"
                  >
                    Time
                  </text>

                  {/* Path */}
                  <polyline
                    fill="none"
                    stroke="#2563EB"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={chartData.path}
                  />

                  {/* Threshold lines (temperature min/max) */}
                  {chartData && typeof effectiveTempMin === 'number' && (() => {
                    const span = chartData.maxTemp - chartData.minTemp || 1;
                    const innerHeight =
                      chartData.height -
                      chartData.paddingTop -
                      chartData.paddingBottom;
                    const ratio = (effectiveTempMin - chartData.minTemp) / span;
                    const clamped = Math.max(0, Math.min(1, ratio));
                    const y =
                      chartData.paddingTop +
                      (1 - clamped) * innerHeight;
                    const midX =
                      (chartData.paddingLeft + (chartData.width - 10)) / 2;
                    return (
                      <g>
                        <line
                          x1={chartData.paddingLeft}
                          y1={y}
                          x2={chartData.width - 10}
                          y2={y}
                          stroke="#22c55e"
                          strokeWidth="0.6"
                          strokeDasharray="4 3"
                        />
                        <text
                          x={midX}
                          y={y - 2}
                          textAnchor="middle"
                          fontSize="6"
                          fill="#16a34a"
                        >
                          {`${effectiveTempMin}°C`}
                        </text>
                      </g>
                    );
                  })()}

                  {chartData && typeof effectiveTempMax === 'number' && (() => {
                    const span = chartData.maxTemp - chartData.minTemp || 1;
                    const innerHeight =
                      chartData.height -
                      chartData.paddingTop -
                      chartData.paddingBottom;
                    const ratio = (effectiveTempMax - chartData.minTemp) / span;
                    const clamped = Math.max(0, Math.min(1, ratio));
                    const y =
                      chartData.paddingTop +
                      (1 - clamped) * innerHeight;
                    const midX =
                      (chartData.paddingLeft + (chartData.width - 10)) / 2;
                    return (
                      <g>
                        <line
                          x1={chartData.paddingLeft}
                          y1={y}
                          x2={chartData.width - 10}
                          y2={y}
                          stroke="#22c55e"
                          strokeWidth="0.6"
                          strokeDasharray="4 3"
                        />
                        <text
                          x={midX}
                          y={y - 2}
                          textAnchor="middle"
                          fontSize="6"
                          fill="#16a34a"
                        >
                          {`${effectiveTempMax}°C`}
                        </text>
                      </g>
                    );
                  })()}

                  {/* Points + hover zones */}
                  {chartData.samples.map(({ x, y, point }) => (
                    <g key={point.created_at}>
                      <circle cx={x} cy={y} r={1.2} fill="#2563EB" />
                      <circle
                        cx={x}
                        cy={y}
                        r={6}
                        fill="transparent"
                        onMouseEnter={() => setHoverPoint({ x, y, point })}
                      />
                    </g>
                  ))}

                  {/* Latest point marker */}
                  {chartData.latest && (() => {
                    const t = new Date(chartData.latest.created_at).getTime();
                    const x =
                      chartData.paddingLeft +
                      ((t - chartData.minTime) /
                        (chartData.maxTime - chartData.minTime || 1)) *
                        (chartData.width -
                          chartData.paddingLeft -
                          10 ||
                          1);
                    const y =
                      chartData.paddingTop +
                      (1 -
                        (chartData.latest.temperature - chartData.minTemp) /
                          (chartData.maxTemp - chartData.minTemp || 1)) *
                        (chartData.height -
                          chartData.paddingTop -
                          chartData.paddingBottom ||
                          1);
                    return (
                      <circle
                        cx={x}
                        cy={y}
                        r={1.6}
                        fill="#2563EB"
                        stroke="#FFFFFF"
                        strokeWidth={1}
                      />
                    );
                  })()}

                  {/* Tooltip */}
                  {hoverPoint && (() => {
                    const d = new Date(hoverPoint.point.created_at);
                    const datePart = `${d
                      .getDate()
                      .toString()
                      .padStart(2, '0')}/${(d.getMonth() + 1)
                      .toString()
                      .padStart(2, '0')}`;
                    const timePart = `${d
                      .getHours()
                      .toString()
                      .padStart(2, '0')}:${d
                      .getMinutes()
                      .toString()
                      .padStart(2, '0')}`;
                    const label = `${hoverPoint.point.temperature.toFixed(1)}°C ${datePart},${timePart}`;

                    // Approximate text width so the tooltip frame כמעט צמוד לטקסט
                    const paddingX = 1;
                    const approxCharWidth = 2.4;
                    const boxWidth =
                      Math.max(40, paddingX * 2 + label.length * approxCharWidth - 4);
                    const boxHeight = 11;
                    const margin = 4;
                    let boxX = hoverPoint.x + margin;
                    let boxY = hoverPoint.y - boxHeight - margin;
                    if (boxX + boxWidth > chartData.width - 4) {
                      boxX = hoverPoint.x - boxWidth - margin;
                    }
                    if (boxY < 4) {
                      boxY = hoverPoint.y + margin;
                    }
                    return (
                      <g>
                        <rect
                          x={boxX}
                          y={boxY}
                          width={boxWidth}
                          height={boxHeight}
                          rx={2}
                          ry={2}
                          fill="#111827"
                          opacity={0.85}
                        />
                        <text
                          x={boxX + paddingX}
                          y={boxY + 7.5}
                          fontSize="4"
                          fontWeight="600"
                          fill="#F9FAFB"
                        >
                          {label}
                        </text>
                      </g>
                    );
                  })()}
                </svg>
              </div>

              <p className="text-[11px] text-traqpack-gray-500">
                The graph shows temperature over time for the selected device.
                When the battery reaches 0%, the history is cleared and a new
                cycle begins from the next reported readings.
              </p>
            </div>
          ) : null}
        </div>

      </div>
    </div>,
    document.body,
  );
};

export default TemperatureHistoryModal;

